import React, { useState, useEffect } from 'react';
import { DollarSign, TrendingUp, Calendar as CalendarIcon, AlertCircle } from 'lucide-react';

const BettingDashboard = ({ userData, existingBets, onSubmit }) => {
  const [bets, setBets] = useState({
    bet1_choice: existingBets?.bet1_choice || 'yes',
    bet1_wager: existingBets?.bet1_wager || 0,
    bet2_choice: existingBets?.bet2_choice || 'yes',
    bet2_wager: existingBets?.bet2_wager || 0,
    bet3_date: existingBets?.bet3_date || '',
    bet3_wager: existingBets?.bet3_wager || 0
  });

  const [error, setError] = useState('');

  const totalWager = bets.bet1_wager + bets.bet2_wager + bets.bet3_wager;
  const remaining = 100 - totalWager;

  const generateDateOptions = () => {
    const options = [];
    const today = new Date();
    
    for (let i = 1; i <= 30; i++) {
      const date = new Date(today);
      date.setDate(today.getDate() + i);
      const dateStr = date.toISOString().split('T')[0];
      const displayStr = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
      options.push({ value: dateStr, label: displayStr });
    }
    
    const after30 = new Date(today);
    after30.setDate(today.getDate() + 31);
    options.push({ value: 'after-30', label: 'After ' + after30.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) });
    
    return options;
  };

  const dateOptions = generateDateOptions();

  const handleWagerChange = (betKey, value) => {
    // Allow empty string to prevent 0-padding
    if (value === '') {
      setBets({
        ...bets,
        [betKey]: 0
      });
      setError('');
      return;
    }
    const numValue = parseFloat(value) || 0;
    setBets({
      ...bets,
      [betKey]: Math.max(0, numValue)
    });
    setError('');
  };

  const handleChoiceChange = (betKey, value) => {
    setBets({
      ...bets,
      [betKey]: value
    });
  };

  const handleSubmit = () => {
    if (totalWager > 100) {
      setError('Total wager cannot exceed $100');
      return;
    }

    if (totalWager === 0) {
      setError('You must place at least one bet');
      return;
    }

    if (!bets.bet3_date) {
      setError('Please select a date for Bet #3');
      return;
    }

    onSubmit({
      ...bets,
      total_wager: totalWager
    });
  };

  return (
    <div className="min-h-screen p-6" style={{ background: 'linear-gradient(135deg, #0A0512 0%, #11081A 100%)' }}>
      <div className="container mx-auto max-w-4xl">
        {/* Header */}
        <div className="mb-8 fade-in">
          <h1 className="text-4xl sm:text-5xl font-black tracking-tighter mb-2">
            Place Your Bets
          </h1>
          <p className="text-[#A19EB5]">
            Wagering as <strong className="text-white">{userData.email}</strong>
          </p>
        </div>

        {/* Budget Tracker */}
        <div className="dashboard-card p-6 mb-8 fade-in" style={{ animationDelay: '0.1s' }}>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-[#A64DF7]" />
              <span className="text-xs uppercase tracking-[0.2em] text-[#A64DF7] font-semibold">
                Budget Tracker
              </span>
            </div>
            <div className="text-right">
              <div className="text-2xl font-bold">
                ${remaining.toFixed(0)}
                <span className="text-sm text-[#A19EB5] ml-2">remaining</span>
              </div>
            </div>
          </div>
          <div className="wager-progress">
            <div 
              className="wager-progress-fill"
              style={{ width: `${totalWager}%` }}
            ></div>
          </div>
          <div className="flex justify-between mt-2 text-xs text-[#6E6885]">
            <span>$0</span>
            <span className="text-[#A64DF7]">${totalWager.toFixed(0)} wagered</span>
            <span>$100</span>
          </div>
        </div>

        {/* Bet #1 */}
        <div className="dashboard-card p-6 mb-6 fade-in" style={{ animationDelay: '0.2s' }}>
          <div className="mb-4">
            <div className="text-xs uppercase tracking-[0.2em] text-[#A64DF7] mb-2 font-semibold">
              Bet #1 • April Launch
            </div>
            <h3 className="text-xl font-bold mb-2">
              Will DeepSeek 4 launch in April 2026?
            </h3>
          </div>

          <div className="grid grid-cols-2 gap-4 mb-4">
            <div
              data-testid="bet1-yes"
              className={`bet-option ${bets.bet1_choice === 'yes' ? 'selected' : ''}`}
              onClick={() => handleChoiceChange('bet1_choice', 'yes')}
            >
              <div className="text-sm uppercase font-bold">Yes</div>
            </div>
            <div
              data-testid="bet1-no"
              className={`bet-option ${bets.bet1_choice === 'no' ? 'selected' : ''}`}
              onClick={() => handleChoiceChange('bet1_choice', 'no')}
            >
              <div className="text-sm uppercase font-bold">No</div>
            </div>
          </div>

          <div>
            <label className="block text-xs uppercase tracking-[0.2em] text-[#A19EB5] mb-2">
              Wager Amount
            </label>
            <div className="relative">
              <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#6E6885]" />
              <input
                data-testid="bet1-wager"
                type="number"
                min="0"
                max="100"
                step="1"
                value={bets.bet1_wager === 0 ? '' : bets.bet1_wager}
                onChange={(e) => handleWagerChange('bet1_wager', e.target.value)}
                className="w-full pl-12"
                placeholder="0"
              />
            </div>
          </div>
        </div>

        {/* Bet #2 */}
        <div className="dashboard-card p-6 mb-6 fade-in" style={{ animationDelay: '0.3s' }}>
          <div className="mb-4">
            <div className="text-xs uppercase tracking-[0.2em] text-[#A64DF7] mb-2 font-semibold">
              Bet #2 • SWE Bench Score
            </div>
            <h3 className="text-xl font-bold mb-2">
              Will DeepSeek 4 exceed 90% on SWE Bench Verified?
            </h3>
          </div>

          <div className="grid grid-cols-2 gap-4 mb-4">
            <div
              data-testid="bet2-yes"
              className={`bet-option ${bets.bet2_choice === 'yes' ? 'selected' : ''}`}
              onClick={() => handleChoiceChange('bet2_choice', 'yes')}
            >
              <div className="text-sm uppercase font-bold">Yes</div>
            </div>
            <div
              data-testid="bet2-no"
              className={`bet-option ${bets.bet2_choice === 'no' ? 'selected' : ''}`}
              onClick={() => handleChoiceChange('bet2_choice', 'no')}
            >
              <div className="text-sm uppercase font-bold">No</div>
            </div>
          </div>

          <div>
            <label className="block text-xs uppercase tracking-[0.2em] text-[#A19EB5] mb-2">
              Wager Amount
            </label>
            <div className="relative">
              <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#6E6885]" />
              <input
                data-testid="bet2-wager"
                type="number"
                min="0"
                max="100"
                step="1"
                value={bets.bet2_wager === 0 ? '' : bets.bet2_wager}
                onChange={(e) => handleWagerChange('bet2_wager', e.target.value)}
                className="w-full pl-12"
                placeholder="0"
              />
            </div>
          </div>
        </div>

        {/* Bet #3 */}
        <div className="dashboard-card p-6 mb-6 fade-in" style={{ animationDelay: '0.4s' }}>
          <div className="mb-4">
            <div className="text-xs uppercase tracking-[0.2em] text-[#A64DF7] mb-2 font-semibold">
              Bet #3 • Exact Release Date
            </div>
            <h3 className="text-xl font-bold mb-2">
              When will DeepSeek 4 be released?
            </h3>
          </div>

          <div className="mb-4">
            <label className="block text-xs uppercase tracking-[0.2em] text-[#A19EB5] mb-2">
              Select Date
            </label>
            <div className="relative">
              <CalendarIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#6E6885]" />
              <select
                data-testid="bet3-date"
                value={bets.bet3_date}
                onChange={(e) => handleChoiceChange('bet3_date', e.target.value)}
                className="w-full pl-12 appearance-none cursor-pointer"
              >
                <option value="">Choose a date...</option>
                {dateOptions.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-xs uppercase tracking-[0.2em] text-[#A19EB5] mb-2">
              Wager Amount
            </label>
            <div className="relative">
              <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#6E6885]" />
              <input
                data-testid="bet3-wager"
                type="number"
                min="0"
                max="100"
                step="1"
                value={bets.bet3_wager === 0 ? '' : bets.bet3_wager}
                onChange={(e) => handleWagerChange('bet3_wager', e.target.value)}
                className="w-full pl-12"
                placeholder="0"
              />
            </div>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="dashboard-card p-4 mb-6 border-[#FF6B6B]/50 bg-[#FF6B6B]/10 fade-in">
            <div className="flex items-center gap-2 text-[#FF6B6B]">
              <AlertCircle className="w-5 h-5" />
              <span>{error}</span>
            </div>
          </div>
        )}

        {/* Submit Button */}
        <button
          data-testid="submit-bets-button"
          onClick={handleSubmit}
          className="purple-btn w-full fade-in"
          style={{ animationDelay: '0.5s' }}
        >
          {existingBets ? 'Update My Bets' : 'Lock In My Bets'}
        </button>

        <p className="mt-4 text-center text-sm text-[#6E6885]">
          You can edit your bets anytime before Monday morning
        </p>
      </div>
    </div>
  );
};

export default BettingDashboard;
